import React, { useState, useEffect } from 'react';
import { Card, Table, Descriptions, Space, Alert, Typography, ConfigProvider, Tabs, Collapse } from 'antd';
import { blue } from '@ant-design/colors';
import { getExploitationInfo } from '../../../Utils/exploitations/exploitations';
import { getValvesByExploitation } from '../../../Utils/exploitations/valves';
import { getAllTanksByExploitation } from '../../../Utils/exploitations/tanks';
import { getPointsByExploitation } from '../../../Utils/exploitations/points';
import { getModemByExploitation } from '../../../Utils/exploitations/modems';
import { getRF_moduleByExploitation } from '../../../Utils/exploitations/RF_module';
import Loading from '../../Shared/Loading';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const fetchExploitationDetails = async (id) => {
    return (await getExploitationInfo(id)).data;
};

const ExpDetails = ({ exploitationId }) => {
    const [exploitation, setExploitation] = useState(null);
    const [product, setProduct] = useState(null);
    const [activeTab, setActiveTab] = useState('1');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const expData = await fetchExploitationDetails(exploitationId);
                setExploitation(expData);
                setProduct({
                    product_name: expData.product.product_name,
                    level1_type_sensors: expData.product.level1_type_sensors?.split(',').filter(sensor => sensor !== ''),
                    level2_type_sensors: expData.product.level2_type_sensors?.split(',').filter(sensor => sensor !== ''),
                });
            } catch (error) {
                console.error("Erreur lors de la récupération des données initiales:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [exploitationId]);

    const fetchTabData = async (tabKey) => {
        if (tabData[tabKey]) return; // Données déjà chargées

        setLoading(true);
        try {
            let data;
            switch (tabKey) {
                case '2':
                    const valves = await getValvesByExploitation(exploitationId);
                    const tanks = await getAllTanksByExploitation(exploitationId);
                    data = { valves, tanks: tanks.data };
                    break;
                case '3':
                    data = (await getPointsByExploitation(exploitationId)).data;
                    break;
                case '4':
                    const modem = await getModemByExploitation(exploitationId);
                    const rfModule = await getRF_moduleByExploitation(exploitationId);
                    data = { modem: modem[0], rfModule: rfModule[0] };
                    break;
                default:
                    break;
            }
            setTabData(prev => ({ ...prev, [tabKey]: data }));
        } catch (error) {
            console.error(`Erreur lors de la récupération des données pour l'onglet ${tabKey}:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        fetchTabData(key);
    };

    if (!exploitation) return loading ? <Loading /> : <Alert message="Erreur lors de la récupération des données" type="error" />;

    const theme = {
        token: {
            colorPrimary: blue[6],
            borderRadius: 8,
            fontFamily: "'Roboto', sans-serif",
        },
    };

    const cardStyle = {
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        marginBottom: '20px',
    };

    const tableStyle = {
        backgroundColor: '#f9f9f9',
    };


  const items = [
    {
      key: '1',
      label: 'Vannes',
      children: (
        <Table
          columns={[
            { title: 'Nom', dataIndex: 'valve_name', key: 'valve_name' },
            { title: 'Superficie nette', dataIndex: 'valve_superficie_net', key: 'valve_superficie_net' },
            { title: 'Culture', dataIndex: 'culture', key: 'culture', render: (text) => {
                if (text === null || text === 'undefined') return 'N/A';
                return text;
            }},
            { title: 'Débit', dataIndex: 'flow', key: 'flow' },
          ]}
          dataSource={tabData[2]?.valves}
          style={tableStyle}
        />
      ),
    },
    {
      key: '2',
      label: 'Réservoirs',
      children: (
        <Table
          columns={[{ title: 'Description', dataIndex: 'tank_description', key: 'tank_description' }]}
          dataSource={tabData[2]?.tanks}
          style={tableStyle}
        />
      ),
    },
  ];



    return (
        <ConfigProvider theme={theme}>
            <Space direction="vertical" size="large" style={{ width: '100%', padding: '20px' }}>
                <Title level={2}>Détails de l'exploitation</Title>

                <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">

                    <TabPane tab="Informations générales" key="1">
                        {loading ? (
                            <Loading />
                        ) : (
                            <>
                                <Card style={cardStyle}>
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Nom">
                                            {exploitation?.exploitation_name !== null && exploitation?.exploitation_name !== "undefined" ? exploitation.exploitation_name : 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Région">
                                            {exploitation?.region !== null && exploitation?.region !== "undefined" ? exploitation.region : 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Ville">
                                            {exploitation?.city !== null && exploitation?.city !== "undefined"? exploitation.city : 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Date d'installation">
                                            {exploitation?.installation_date !== null && exploitation?.installation_date !== "undefined"
                                                ? new Date(exploitation.installation_date).toLocaleDateString('fr-FR')
                                                : 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Superficie nette">
                                            {exploitation?.exploitiation_superficie_net !==  null
                                                ? `${exploitation.exploitiation_superficie_net} ha`
                                                : 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Coordonnées">
                                            {exploitation?.coordinates !==  null ? exploitation.coordinates : 'N/A'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Card title="Détails du produit" style={cardStyle}>
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Nom">
                                            {product?.product_name !== null ? product.product_name : 'N/A'}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Types de capteurs de niveau 1">
                                            <div className="flex flex-wrap gap-3">
                                                {product?.level1_type_sensors?.length > 0 ? (
                                                    product.level1_type_sensors.map((sensor, index) => (
                                                        <ProductSensorCard sensor={sensor} key={index} />
                                                    ))
                                                ) : (
                                                    <p>Aucun capteur disponible</p>
                                                )}
                                            </div>
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Types de capteurs de niveau 2">
                                            <div className="flex flex-wrap gap-3">
                                                {product?.level2_type_sensors?.length > 0 ? (
                                                    product.level2_type_sensors.map((sensor, index) => (
                                                        <ProductSensorCard sensor={sensor} key={index} />
                                                    ))
                                                ) : (
                                                    <p>Aucun capteur disponible</p>
                                                )}
                                            </div>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                            </>
                        )}
                    </TabPane>

                    {exploitation.product.is_irrigation && (
                        <TabPane tab="Irrigation" key="2">
                            {loading ? (
                                <Loading />
                            )
                             : <Collapse defaultActiveKey={['1', '2']} items={items} />
                             }
                        </TabPane>
                    )}

                    {exploitation.product.is_soil && (
                        <TabPane tab="Points de sol" key="3">
                            {loading ? (
                                <Loading />
                            ) : (
                                <Card style={cardStyle}>
                                    <Table
                                        columns={[
                                            { title: 'Numéro du point', dataIndex: 'point_number', key: 'point_number' },
                                            { title: 'Coordonnées', dataIndex: 'coordinates', key: 'coordinates' },
                                            { title: 'Profondeur niveau 1', dataIndex: 'level1_depth', key: 'level1_depth' },
                                            { title: 'Profondeur niveau 2', dataIndex: 'level2_depth', key: 'level2_depth' },
                                        ]}
                                        dataSource={tabData[3]}
                                        style={tableStyle}
                                    />
                                </Card>
                            )}
                        </TabPane>
                    )}

                    <TabPane tab="Équipement" key="4">
                        {loading ? (
                            <Loading />
                        ) : (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Card title="Détails du modem" style={cardStyle}>
                                    {tabData[4]?.modem && (
                                        <Descriptions bordered column={2}>
                                            <Descriptions.Item label="ID manuel">{tabData[4].modem.modem_manual_id}</Descriptions.Item>
                                            <Descriptions.Item label="Numéro de téléphone">{tabData[4].modem.telephone_number}</Descriptions.Item>
                                            <Descriptions.Item label="Opérateur">{tabData[4].modem.operator}</Descriptions.Item>
                                            <Descriptions.Item label="Modèle">{tabData[4].modem.model_name}</Descriptions.Item>
                                        </Descriptions>
                                    )}
                                </Card>

                                <Card title="Détails du module RF" style={cardStyle}>
                                    {tabData[4]?.rfModule && (
                                        <Descriptions bordered column={2}>
                                            <Descriptions.Item label="Adresse de destination">{tabData[4].rfModule.destination_address}</Descriptions.Item>
                                            <Descriptions.Item label="Puissance transmise">{tabData[4].rfModule.transmitted_power}</Descriptions.Item>
                                            <Descriptions.Item label="Classe de vitesse">{tabData[4].rfModule.speed_class}</Descriptions.Item>
                                        </Descriptions>
                                    )}
                                </Card>
                            </Space>
                        )}
                    </TabPane>
                </Tabs>
            </Space>
        </ConfigProvider>
    );
};

const ProductSensorCard = ({ sensor }) => {
    return (
        <div className='border-2 w-16 text-center font-semibold rounded-xl p-2 border-green-500 bg-green-100 py-2'>
            <p>{sensor}</p>
        </div>
    )
}

export default ExpDetails;